/* eslint-disable no-nested-ternary */
import currency from "currency.js";
import { findMatchingSpotCollateral } from "../../../utils/spotCollateral";
import { useCallback, useEffect, useMemo } from "react";
import {
  CollateralAssetResponse,
  OrderTypeResponse,
  SideResponse,
} from "../../../codegen-api";
import { COLORS } from "../../../constants/design/colors";
import { AccountStateEnum } from "../../../contexts/AuthContext";
import { IPositionInfo } from "../../../interfaces/TradeInfo";
import { floorToDecimalsString } from "../../../utils/decimalString";
import { effectiveStepPrecision } from "../../../utils/format";
import { Button, ButtonThemeEnum } from "../../Buttons/styles";
import {
  playOrderCreatedAnimation,
  useMountedRef,
} from "../orderCreatedAnimation";
import { useOptionsTradeFormSetup } from "./useOptionsTradeFormSetup";
import { useOptionsTradeFormComputed } from "./useOptionsTradeFormComputed";

type SetupResult = ReturnType<typeof useOptionsTradeFormSetup>;
type ComputedResult = ReturnType<typeof useOptionsTradeFormComputed>;

export function useOptionsTradeFormPosition({
  orderType,
  currentPosition,
  orderSide,
  accountData,
  accountDataLoading,
  optionsTradeFormTranslations,
  commonFormTranslations,
  notEnoughBalanceError,
  contractPriceStep,
  deleteRFQsByInstrumentId,
  setShowCloseModal,
  selectedInstrument,
  orderbookData,
  amount,
  accountSigningKeyState,
  setShowConnectModal,
  prevOrderbookInstrument,
  setValue,
  animControls,
}: SetupResult & ComputedResult) {
  // Reduce only is disabled if ordertype is limit
  const reduceOnlyDisabled = orderType === OrderTypeResponse.Limit;

  // Reduce only is not allowed if theres no position, or position === orderDirection
  const reduceOnlyNotAllowed = useMemo(
    () =>
      !reduceOnlyDisabled &&
      (!currentPosition || currentPosition.side === orderSide),
    [currentPosition, orderSide, reduceOnlyDisabled]
  );

  const positionInfo = useMemo<IPositionInfo[]>(() => {
    // Options available balance is ONLY using USDC available balance
    // `collateral_asset` is an OBJECT on the wire; comparing the row to the
    // enum never matched, so this read "---" whatever the balance was.
    const optionsAvailableBalance = findMatchingSpotCollateral(
      accountData?.collaterals,
      CollateralAssetResponse.Usdc
    )?.available_balance;

    // Balances are floored to the cent: `currency` rounds half-up, showing more
    // than can actually be spent.
    const balanceInfo: IPositionInfo =
      orderSide === SideResponse.Buy
        ? {
            title: optionsTradeFormTranslations("available_balance"),
            value:
              accountDataLoading || !optionsAvailableBalance
                ? "---"
                : currency(
                    floorToDecimalsString(optionsAvailableBalance, 2)
                  ).format(),
            side: undefined,
            warningOrError: notEnoughBalanceError ? "error" : undefined,
            showErrorIcon: true,
          }
        : {
            title: commonFormTranslations("margin_balance"),
            value: accountData?.available_balance
              ? currency(
                  floorToDecimalsString(accountData.available_balance, 2)
                ).format()
              : "---",
            side: undefined,
            warningOrError: notEnoughBalanceError ? "error" : undefined,
            showErrorIcon: true,
          };

    const posInfo: IPositionInfo[] = [
      balanceInfo,
      {
        title: optionsTradeFormTranslations("current_position"),
        // Floored, never rounded up past the position that can be closed.
        value: currentPosition
          ? floorToDecimalsString(
              currentPosition.amount,
              effectiveStepPrecision(
                contractPriceStep.amount_step,
                contractPriceStep.amount_precision
              )
            )
          : "---",
        side: currentPosition?.side,
      },
    ];

    if (currentPosition) {
      const roi =
        Number(currentPosition.unrealized_pnl) /
        (Number(currentPosition.avg_entry_price) *
          Number(currentPosition.amount));
      posInfo.push({
        title: optionsTradeFormTranslations("position_roi"),
        value: currentPosition ? (
          <span
            style={{
              color:
                Number(currentPosition.unrealized_pnl) >= 0
                  ? COLORS.up.one
                  : COLORS.down.one,
            }}
          >
            {roi >= 0 ? "+" : ""}
            {(roi * 100).toFixed(2)}%
          </span>
        ) : (
          "---"
        ),
      });
    }

    return posInfo;
  }, [
    commonFormTranslations,
    optionsTradeFormTranslations,
    accountDataLoading,
    accountData?.collaterals,
    accountData?.available_balance,
    contractPriceStep.amount_step,
    contractPriceStep.amount_precision,
    notEnoughBalanceError,
    currentPosition,
    orderSide,
  ]);

  const onClosePositionModalHide = useCallback(() => {
    if (currentPosition)
      deleteRFQsByInstrumentId(currentPosition.instrument_id);
    setShowCloseModal(false);
  }, [currentPosition, deleteRFQsByInstrumentId]);

  // Validates available liquidity for market buy/sell
  const insufficientMarketLiquidityWarning = useMemo(() => {
    if (!selectedInstrument || orderType === OrderTypeResponse.Limit) {
      return undefined;
    }

    // if buy, check asks liquidity
    let insufficientLiquidity = false;
    if (orderSide === SideResponse.Buy) {
      const totalAsksSize =
        orderbookData?.asks?.reduce((prev, ask) => {
          const askSize = Number(ask[1] || 0);
          return prev + askSize;
        }, 0) || 0;
      if (totalAsksSize < parseFloat(amount)) {
        insufficientLiquidity = true;
      }
    }

    // if sell, check bids liquidity
    if (orderSide === SideResponse.Sell) {
      const totalBidsSize =
        orderbookData?.bids?.reduce((prev, ask) => {
          const bidSize = Number(ask[1] || 0);
          return prev + bidSize;
        }, 0) || 0;
      if (totalBidsSize < parseFloat(amount)) {
        insufficientLiquidity = true;
      }
    }

    if (insufficientLiquidity) {
      return optionsTradeFormTranslations("insufficient_liquidity");
    }
    return undefined;
  }, [
    optionsTradeFormTranslations,
    selectedInstrument,
    orderType,
    orderSide,
    orderbookData?.asks,
    orderbookData?.bids,
    amount,
  ]);

  const inactiveSubmitButtonState = useMemo(
    () => (
      <Button
        fullWidth
        type="button"
        buttonTheme={ButtonThemeEnum.HIGHLIGHT}
        onClick={() => setShowConnectModal(true)}
      >
        {accountSigningKeyState === AccountStateEnum.REQUIRE_PASSWORD
          ? commonFormTranslations("unlock_trading")
          : accountSigningKeyState === AccountStateEnum.OK
          ? commonFormTranslations("continue_onboarding")
          : accountSigningKeyState === AccountStateEnum.REQUIRE_CONNECT
          ? commonFormTranslations("connect_wallet")
          : commonFormTranslations("complete_sign_in")}
      </Button>
    ),
    [accountSigningKeyState, commonFormTranslations, setShowConnectModal]
  );

  // Update default price for limit order
  useEffect(() => {
    // We're comparing the current value and the previous one to prevent the UI from
    // resetting the price/amount values in between renders
    if (
      orderbookData?.instrument_name &&
      prevOrderbookInstrument !== orderbookData?.instrument_name
    ) {
      // Update default values for the selected instrument
      let defaultPrice = "0";
      defaultPrice =
        orderSide === SideResponse.Buy
          ? orderbookData?.bids?.[0]?.[0] || "0"
          : orderbookData?.asks?.[0]?.[0] || "0";

      if (Number(defaultPrice)) {
        setValue("price", defaultPrice);
      } else {
        setValue("price", "");
      }
    }
  }, [
    orderSide,
    orderbookData?.asks,
    orderbookData?.bids,
    orderbookData?.instrument_name,
    prevOrderbookInstrument,
    setValue,
  ]);

  const formMounted = useMountedRef();
  const showOrderCreatedAnimation = useCallback(
    () => playOrderCreatedAnimation(animControls, () => formMounted.current),
    [animControls, formMounted]
  );

  return {
    reduceOnlyDisabled,
    reduceOnlyNotAllowed,
    positionInfo,
    onClosePositionModalHide,
    insufficientMarketLiquidityWarning,
    inactiveSubmitButtonState,
    showOrderCreatedAnimation,
  };
}
